<template>
  <div class="group relative mb-6 w-full">
    <DatePicker
      v-bind="dynamicIdAttr"
      ref="datepicker"
      :class="[
        hasError ? 'border-error' : 'border-darkestGray',
        disabledClasses,
      ]"
      :disabled="disabled"
      :readonly="readonly"
      :model-value="formatedValue"
      :min-date="min"
      :max-date="max"
      :enable-time-picker="false"
      :flow="flow"
      :name="name"
      :placeholder="placeholder"
      :locale="locale"
      :format="displayFormat"
      :teleport="true"
      no-today
      @update:model-value="onUpdateModelValue"
    >
      <template #action-row="{ internalModelValue, selectDate }">
        <div class="action-row w-full">
          <p class="pb-3 text-center font-medium text-primary">
            {{ internalModelValue ? formatDate(internalModelValue) : '' }}
          </p>
          <div class="flex space-x-2">
            <BaseButton theme="white" class="w-1/2 py-1" @click="onCloseMenu">
              {{ $t('action.cancel') }}
            </BaseButton>
            <BaseButton
              theme="primary"
              class="w-1/2 py-1"
              :class="{
                'pointer-events-none bg-opacity-20': !internalModelValue,
              }"
              @click="selectDate"
            >
              {{ $t('action.choose') }}
            </BaseButton>
          </div>
        </div>
      </template>
    </DatePicker>

    <slot />

    <label
      v-bind="dynamicForAttr"
      class="absolute top-3 -z-10 origin-[0] -translate-y-6 scale-100 transform text-xs font-semibold uppercase tracking-wider text-darkestGray duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-6 peer-focus:scale-100 md:text-sm"
      :class="disabled ? 'opacity-25' : ''"
    >
      {{ label }}
      <span v-if="isRequired" class="!text-[#eb5757]">*</span>
    </label>

    <BaseErrorMessage v-if="hasError">
      <div class="inline-flex items-center space-x-2">
        <inline-svg
          :src="useAsset(`icons/base/error.svg`)"
          loading="lazy"
          alt="error-icon"
        />
        <span>
          {{ $t(errorMsg) }}
        </span>
      </div>
    </BaseErrorMessage>

    <slot name="errors"></slot>
  </div>
</template>

<script setup>
import moment from 'moment'
import InlineSvg from 'vue-inline-svg'

const emit = defineEmits(['input', 'update:value'])
const props = defineProps({
  value: {
    type: [String, Number, null],
    required: true,
  },
  id: {
    type: [String],
    default: '',
  },
  name: {
    type: [String],
    default: '',
  },
  label: {
    type: [String],
    default: '',
  },
  placeholder: {
    type: [String],
    default: '',
  },
  disabled: {
    type: [Boolean],
    default: false,
  },
  readonly: {
    type: [Boolean],
    default: false,
  },
  anyDirty: {
    type: [Boolean],
    default: true,
  },
  errors: {
    type: [Object],
    default: null,
  },
  isRequired: {
    type: [Boolean],
    default: false,
  },
  min: {
    type: [String, Date],
    default: null,
  },
  max: {
    type: [String, Date],
    default: () => new Date(),
  },
  flow: {
    type: [Array],
    default: () => ['year', 'month', 'calendar'],
  },
  displayFormat: {
    type: [String],
    default: 'dd.MM.yyyy',
  },
  outputFormat: {
    type: [String],
    default: 'YYYY-MM-DD',
  },
})

const isMobile = useMedia('(max-width: 1023px)')
const { locale: localeI18n } = useI18n()
const datepicker = ref(null)

const locale = computed(() => {
  if (localeI18n.value === 'uk') return 'ua'
  return localeI18n.value
})

const formatedValue = computed(() => {
  return moment(props.value, props.outputFormat).toString()
})

const disabledClasses = computed(() => {
  return props.disabled || props.readonly ? 'opacity-25' : ''
})

const dynamicIdAttr = computed(() => {
  return props.id ? { id: props.id } : {}
})

const dynamicForAttr = computed(() => {
  return props.id ? { for: props.id } : {}
})

const hasError = computed(() => {
  return props.anyDirty && props.errors && props.errors?.[props.name]
})

const errorMsg = computed(() => {
  return props.errors[props.name] instanceof Array
    ? props.errors[props.name][0]
    : props.errors[props.name]
})

const onUpdateModelValue = (value) => {
  if (value === null) {
    emit('update:value', '')
    return emit('input', '')
  }

  const formattedValue = moment(value).format(props.outputFormat)

  emit('update:value', formattedValue)
  emit('input', formattedValue)
}

const onCloseMenu = () => {
  datepicker.value.closeMenu()
}

const formatDate = (date) => {
  return moment(date).format('DD.MM.YYYY')
}
</script>
